import React from "react";
import { IconProps } from "react-feather";
import { LeadActivity } from "model/lead-activity";
import { LeadActivityType } from "type/lead-activity-type";
import { SparkleFeatherIcon } from "component/shared/sparkle-icon";

/** The Search Agent's own activity types, as opposed to activity attributed to it. */
const searchAgentTypes = [
	LeadActivityType.SEARCH_AGENT_SESSION_STARTED,
	LeadActivityType.SEARCH_PROFILE_GENERATED,
];

/** Whether the activity belongs under the Search Agent filter. */
export const isSearchAgentActivity = (activity: LeadActivity) => {
	return activity.viaSearchAgent || searchAgentTypes.includes(activity.type);
};

/** The card phrase. Attribution never changes the type, so it is appended rather than swapped in. */
export const getLeadActivityPhrase = (activity: LeadActivity) => {
	return activity.viaSearchAgent
		? `${activity.type.phrase} via Search Agent`
		: activity.type.phrase;
};

export const getLeadActivityIcon = (activity: LeadActivity): React.ComponentType<IconProps> => {
	return activity.viaSearchAgent ? SparkleFeatherIcon : activity.type.icon;
};

export interface LeadActivityFilterOption {
	/** A type id, or one of the ids below. */
	id: number;
	label: string;
	icon: React.ComponentType<IconProps>;
}

export const ALL_ACTIVITY_FILTER_ID = 0;
/** Negative so it can never collide with a type id. */
export const SEARCH_AGENT_FILTER_ID = -1;

export const searchAgentFilterOption: LeadActivityFilterOption = {
	id: SEARCH_AGENT_FILTER_ID,
	label: "Search Agent",
	icon: SparkleFeatherIcon,
};

/**
 * One option per distinct type in the activities, in the order first seen. With the Search Agent
 * option shown, the Search Agent's own types are folded into it instead of listed again; without
 * it they keep their own options, so that nothing on screen becomes unfilterable.
 */
export const getLeadActivityFilterOptions = (activities: LeadActivity[], showSearchAgent: boolean) => {
	const types = Array.from(new Set(activities.map((activity) => activity.type)));
	const options: LeadActivityFilterOption[] = types
		.filter((type) => !showSearchAgent || !searchAgentTypes.includes(type))
		.map(({ id, label, icon }) => ({ id, label, icon }));
	if (showSearchAgent && activities.some(isSearchAgentActivity)) {
		options.unshift(searchAgentFilterOption);
	}
	return options;
};

/** Attributed activity matches both the Search Agent option and its own type's option. */
export const filterLeadActivities = (activities: LeadActivity[], filterId: number) => {
	if (filterId === ALL_ACTIVITY_FILTER_ID) {
		return activities;
	}
	if (filterId === SEARCH_AGENT_FILTER_ID) {
		return activities.filter(isSearchAgentActivity);
	}
	return activities.filter((activity) => activity.type.id === filterId);
};
